import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { LanguageContext } from '../../../context/LanguageContext';
import Layout from '../../../components/admin/Layout';
import { BiEdit, BiTrash } from 'react-icons/bi';

const UsersIndex = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isRTL, t } = useContext(LanguageContext);

  // Effect for loading users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/users');
        setUsers(response.data.users || []);
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error(t('admin.failedToLoadUsers'));
        setUsers([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [t]); // Include t since it's used for error messages

  const handleToggleStatus = async (user) => {
    try {
      await axios.put(`/users/${user.id}/toggle-status`);
      // Update user status in state directly
      setUsers(currentUsers => 
        currentUsers.map(u => 
          u.id === user.id ? { ...u, is_active: !u.is_active } : u
        )
      );
      toast.success(t(user.is_active ? 'admin.userDeactivated' : 'admin.userActivated'));
    } catch (error) {
      toast.error(t('admin.failedToUpdateUserStatus'));
      console.error('Error updating user status:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('admin.confirmDeleteUser'))) {
      return;
    }

    try {
      await axios.delete(`/users/${userId}`);
      // Update users state directly
      setUsers(currentUsers => currentUsers.filter(user => user.id !== userId));
      toast.success(t('admin.userDeleted'));
    } catch (error) {
      toast.error(t('admin.failedToDeleteUser'));
      console.error('Error deleting user:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{t('admin.usersManagement')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('admin.manageUsers')}</p>
          </div>
          <Link
            to="/admin/users/create"
            className={`inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg 
              shadow-sm hover:bg-blue-600 transform hover:translate-y-[-1px] transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {t('admin.createUser')}
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {isRTL ? 'رقم المستخدم' : 'User Number'}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('admin.name')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('admin.email')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('admin.role')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('admin.status')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    1 checked
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('admin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users && users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm font-medium text-gray-900" dir="ltr">{user.user_number}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {t(`admin.roles.${user.role}`)}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {t(user.is_active ? 'admin.active' : 'admin.inactive')}
                        </button>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.is_one_checked
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.is_one_checked ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-4' : 'space-x-4'}`}>
                          <Link
                            to={`/admin/users/${user.id}/edit`}
                            className="text-indigo-600 hover:text-indigo-900"
                            title={t('admin.edit')}
                          >
                            <BiEdit className="text-xl" />
                          </Link>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('admin.delete')}
                          >
                            <BiTrash className="text-xl" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                      {t('admin.noUsersFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UsersIndex;
